import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import gsap from "gsap";

require("../categories-content/categories-content");

const STORAGE_KEY = "nm_portfolio_content";
const CATEGORIES_PER_PAGE = 3;
const FILTER_ALL = "all";

/* eslint-disable global-require */
const DEFAULT_IMAGES = [
  require("../info-preview/img/info-preview-01.jpg"),
  require("../info-preview/img/info-preview-02.jpg"),
  require("../info-preview/img/info-preview-03.jpg"),
];
/* eslint-enable global-require */

const portfolioPageState = {
  categories: [],
  projects: [],
  filterKey: FILTER_ALL,
  page: 1,
};

function categoriesStore() {
  return window.NMCategoriesContent;
}

const DEFAULT_CATEGORIES = [
  { key: "outdoor", title: "Наружная реклама" },
  { key: "interior", title: "Интерьерная реклама" },
  { key: "transport", title: "Реклама в общественном транспорте" },
  { key: "transport-branding", title: "Брендирование транспорта" },
  { key: "art-objects", title: "Арт-объекты, стелы, инсталяции" },
  { key: "museums", title: "Оформление музеев и кабинетов охраны труда" },
  { key: "education", title: "Оформление учебных заведений" },
  { key: "honor-boards", title: "изготовление и монтаж досок почета" },
  { key: "polygraphy", title: "Полиграфическая продукция" },
  { key: "souvenirs", title: "Сувенирная продукция" },
  { key: "wide-format", title: "Широкоформатная печать" },
  { key: "milling-laser", title: "Фрезерная и лазерная резка материала" },
  { key: "branding-digital", title: "Брендинг, маркетинг, Digital" },
];

const DEFAULT_DESCRIPTION = `По заказу администрации Батыревского района чувашской области была изготовлена доска почета.

Эксклюзивный проект доски почета с 3D-визуализацией, техническими чертежами и расчетами создан специалистами компании «Новая Марка».

Производство и монтаж — написать описание проведенных работ.

Доска почета имеет следующие размеры:
— Длина — 13 метров;
— Высота — 3,5 метра.`;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function buildTitleHtml(title) {
  const words = String(title || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1) return `<span>${escapeHtml(words[0])}</span>`;
  return `<span>${escapeHtml(words[0])}</span> ${escapeHtml(words.slice(1).join(" "))}`;
}

function normalizeCategory(item = {}) {
  return categoriesStore().normalizeCategory(item);
}

function getDefaultCategories() {
  return categoriesStore().getCategories().map((item) => ({
    key: item.key,
    title: item.title,
    titleHtml: item.titleHtml,
  }));
}

function categoriesToMap(categories) {
  return (categories || []).reduce((acc, item) => {
    acc[item.key] = item;
    return acc;
  }, {});
}

function createDefaultProject(overrides = {}, index = 0, categoryKeys = DEFAULT_CATEGORIES.map((item) => item.key)) {
  const keys = categoryKeys.length ? categoryKeys : DEFAULT_CATEGORIES.map((item) => item.key);
  return {
    id: overrides.id || `portfolio-${index + 1}`,
    title: overrides.title || "Производство арт-объекта для города Сердобска",
    shortDesc: overrides.shortDesc || "Установочные и монтажные работы",
    category: overrides.category || keys[index % keys.length],
    image: overrides.image || "",
    imageIndex: Number.isFinite(Number(overrides.imageIndex))
      ? Number(overrides.imageIndex)
      : index % DEFAULT_IMAGES.length,
    gallery: Array.isArray(overrides.gallery) ? overrides.gallery : [],
    description: overrides.description || DEFAULT_DESCRIPTION,
    showOnHome: overrides.showOnHome !== false,
  };
}

function getDefaultData() {
  const categories = getDefaultCategories();
  const categoryKeys = categories.length
    ? categories.map((item) => item.key)
    : DEFAULT_CATEGORIES.map((item) => item.key);
  return {
    projects: [
      createDefaultProject({ id: "p1", category: "outdoor", showOnHome: true }, 0, categoryKeys),
      createDefaultProject({ id: "p2", category: "outdoor", showOnHome: true }, 1, categoryKeys),
      createDefaultProject({ id: "p3", category: "interior", showOnHome: true }, 2, categoryKeys),
      createDefaultProject({ id: "p4", category: "interior", showOnHome: true }, 0, categoryKeys),
      createDefaultProject({ id: "p5", category: "transport", showOnHome: true }, 1, categoryKeys),
      createDefaultProject({ id: "p6", category: "transport", showOnHome: true }, 2, categoryKeys),
    ],
  };
}

function normalizeProject(item = {}, index = 0, categoryKeys = []) {
  const keys = categoryKeys.length
    ? categoryKeys
    : DEFAULT_CATEGORIES.map((entry) => entry.key);
  const category = keys.includes(item.category)
    ? item.category
    : keys[index % keys.length];

  const imageIndex = Number.isFinite(Number(item.imageIndex))
    ? Math.max(0, Math.min(DEFAULT_IMAGES.length - 1, Number(item.imageIndex)))
    : index % DEFAULT_IMAGES.length;

  const gallery = Array.isArray(item.gallery)
    ? item.gallery.map((src) => String(src || "").trim()).filter(Boolean)
    : [];

  return {
    id: String(item.id || createId()),
    title: String(item.title || "").trim(),
    shortDesc: String(item.shortDesc || "").trim(),
    category,
    image: String(item.image || "").trim(),
    imageIndex,
    gallery,
    description: String(item.description || "").trim(),
    showOnHome: Boolean(item.showOnHome),
  };
}

function normalizeData(raw) {
  const fallback = getDefaultData();
  const categories = getDefaultCategories();
  const categoryKeys = categories.length
    ? categories.map((item) => item.key)
    : DEFAULT_CATEGORIES.map((item) => item.key);

  if (!raw || typeof raw !== "object") {
    return { projects: fallback.projects, categories };
  }

  const projects = Array.isArray(raw.projects) && raw.projects.length
    ? raw.projects
      .map((item, index) => normalizeProject(item, index, categoryKeys))
      .filter((item) => item.title)
    : fallback.projects.map((item) => ({ ...item }));

  return { projects, categories };
}

function getPortfolioData() {
  const cached = window.NMApi?.cache?.portfolio;
  if (cached) return normalizeData(cached);
  return { ...getDefaultData(), categories: getDefaultCategories() };
}

function savePortfolioData(data) {
  const normalized = normalizeData(data);
  if (window.NMApi) {
    window.NMApi.cache.portfolio = { projects: normalized.projects };
    window.NMApi.save("portfolio", { projects: normalized.projects }).catch((error) => {
      window.alert(error.message || "Не удалось сохранить портфолио"); // eslint-disable-line no-alert
    });
  }
  return normalized;
}

function getCategories() {
  return getDefaultCategories();
}

function getCategoryMap() {
  return categoriesToMap(getCategories());
}

function getCategoryKeys() {
  return getCategories().map((item) => item.key);
}

function reassignProjectsCategory(deletedKey, fallbackKey) {
  if (!deletedKey) return getPortfolioData();
  const data = getPortfolioData();
  const nextFallback = fallbackKey || getCategoryKeys().find((key) => key !== deletedKey) || "";
  data.projects = data.projects.map((project) => (
    project.category === deletedKey
      ? { ...project, category: nextFallback || project.category }
      : project
  ));
  return savePortfolioData(data);
}

function resolveProjectImage(project, index = 0) {
  if (project?.image) return project.image;
  const imageIndex = Number.isFinite(Number(project?.imageIndex))
    ? Number(project.imageIndex)
    : index;
  return DEFAULT_IMAGES[imageIndex] || DEFAULT_IMAGES[0] || "";
}

function resolveGallery(project, index = 0) {
  const cover = resolveProjectImage(project, index);
  const gallery = Array.isArray(project?.gallery)
    ? project.gallery.filter(Boolean)
    : [];
  if (gallery.length) return gallery;
  return cover ? [cover] : [];
}

function descriptionToHtml(text) {
  const chunks = String(text || "")
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  if (!chunks.length) {
    return "<p>Описание проекта пока не добавлено.</p>";
  }

  return chunks
    .map((chunk) => {
      const lines = chunk.split("\n").map((line) => line.trim()).filter(Boolean);
      const isList = lines.length > 1 && lines.every((line) => /^[—\-•]/.test(line));
      if (isList) {
        const items = lines
          .map((line) => `<li>${escapeHtml(line.replace(/^[—\-•]\s*/, ""))}</li>`)
          .join("");
        return `<ul>${items}</ul>`;
      }
      if (lines.length === 1 && lines[0].length < 120) {
        return `<strong>${escapeHtml(lines[0])}</strong>`;
      }
      return `<p>${escapeHtml(chunk).replace(/\n/g, "<br>")}</p>`;
    })
    .join("");
}

function renderPreviewCard(project, index = 0) {
  return `
    <div class="slide-row__slide swiper-slide">
      <div class="info-preview" data-portfolio-project-id="${escapeHtml(project.id)}">
        <div class="info-preview__img">
          <img src="${escapeHtml(resolveProjectImage(project, index))}" alt="${escapeHtml(project.title)}">
        </div>
        <div class="info-preview__content">
          <h5 class="info-preview__title h5">${escapeHtml(project.title)}</h5>
          <p class="info-preview__desc d2">${escapeHtml(project.shortDesc || "")}</p>
          <button class="info-preview__btn btn btn_arrow" type="button" aria-label="Открыть проект">
            <svg width="32" height="32" viewBox="0 0 32 32">
              <use xlink:href="#icon-arrow-right"></use>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;
}

function fillPortfolioPopup(project, index = 0) {
  const popup = document.getElementById("popup-portfolio");
  if (!popup || !project) return;

  const wrapper = popup.querySelector(".popup-portfolio__swiper-wrapper");
  const content = popup.querySelector(".popup-portfolio__content .user-content");
  const gallery = resolveGallery(project, index);

  if (wrapper) {
    wrapper.innerHTML = gallery.map((src) => `
      <div class="popup-portfolio__slide swiper-slide">
        <div class="popup-portfolio__slide-img" data-swiper-parallax="40%"
             style="background-image: url('${escapeHtml(src)}')">
        </div>
      </div>
    `).join("");
  }

  if (content) {
    content.innerHTML = descriptionToHtml(project.description);
  }

  const swiperEl = popup.querySelector(".popup-portfolio__swiper");
  if (swiperEl?.swiper) {
    swiperEl.swiper.update();
    swiperEl.swiper.slideTo(0, 0);
  }
}

let portfolioPopupOpening = false;

function openPortfolioProject(projectId, projects) {
  if (portfolioPopupOpening) return;
  const list = projects || portfolioPageState.projects || [];
  const index = list.findIndex((item) => item.id === projectId);
  const project = list[index];
  if (!project) return;

  portfolioPopupOpening = true;
  fillPortfolioPopup(project, index >= 0 ? index : 0);
  if (typeof window.openPopup === "function") {
    window.openPopup("popup-portfolio");
  }
  window.setTimeout(() => {
    portfolioPopupOpening = false;
  }, 300);
}

function bindProjectCards(root, projects) {
  if (!root) return;
  root.querySelectorAll("[data-portfolio-project-id]").forEach((card) => {
    card.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      openPortfolioProject(card.getAttribute("data-portfolio-project-id"), projects);
    });
  });
}

function renderProjectList(wrapper, projects) {
  if (!wrapper) return;
  const root = wrapper;

  if (!projects.length) {
    root.innerHTML = `
      <div class="slide-row__slide swiper-slide">
        <div class="info-preview">
          <div class="info-preview__content">
            <h5 class="info-preview__title h5">Пока нет проектов</h5>
            <p class="info-preview__desc d2">Добавьте работы в разделе «Контент»</p>
          </div>
        </div>
      </div>
    `;
    return;
  }

  root.innerHTML = projects.map((project, index) => renderPreviewCard(project, index)).join("");
  bindProjectCards(root, projects);
}

function renderHomePortfolio(projects) {
  const wrapper = document.querySelector("[data-portfolio-home]");
  if (!wrapper) return;

  const homeProjects = projects.filter((item) => item.showOnHome);
  renderProjectList(wrapper, homeProjects.length ? homeProjects : projects.slice(0, 6));
}

function collectProjectsByCategoryKeys(categoryKeys) {
  const data = getPortfolioData();
  const categories = getCategories();
  const keys = (Array.isArray(categoryKeys) ? categoryKeys : [categoryKeys])
    .filter(Boolean)
    .map((item) => (typeof item === "object" ? (item.key || item.slug || item.id) : item))
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  if (!keys.length) return [];

  const matchKeys = new Set();
  keys.forEach((key) => {
    matchKeys.add(key);
    const category = categories.find((item) => (
      item.key === key || item.slug === key || item.id === key
    ));
    if (category) {
      matchKeys.add(category.key);
      matchKeys.add(category.slug);
      matchKeys.add(category.id);
    }
  });

  return (data.projects || []).filter((item) => matchKeys.has(item.category));
}

function syncPortfolioSectionSwiper(wrapper, section) {
  const syncSwiper = () => {
    const swiperRoot = wrapper.closest(".swiper");
    const currentCount = section?.querySelector(".custom-count__current");
    if (swiperRoot?.swiper) {
      swiperRoot.swiper.update();
      swiperRoot.swiper.slideTo(0, 0);
    }
    if (currentCount) currentCount.textContent = "01";
  };

  syncSwiper();
  window.setTimeout(syncSwiper, 0);
  window.setTimeout(syncSwiper, 150);
}

function renderServicePortfolio(categoryOrKey) {
  const wrapper = document.querySelector("[data-service-portfolio-slides]");
  if (!wrapper) return;

  const section = wrapper.closest(".portfolio-section");
  const projects = collectProjectsByCategoryKeys(
    categoryOrKey && typeof categoryOrKey === "object"
      ? [categoryOrKey.key || categoryOrKey.slug || categoryOrKey.id]
      : [categoryOrKey],
  );

  if (!projects.length) {
    if (section) section.hidden = true;
    wrapper.innerHTML = "";
    return;
  }

  if (section) section.hidden = false;
  renderProjectList(wrapper, projects);
  syncPortfolioSectionSwiper(wrapper, section);
}

function renderArticlesPortfolio(categoryKeys) {
  const wrapper = document.querySelector("[data-article-portfolio-slides]")
    || document.querySelector("[data-service-portfolio-slides]");
  if (!wrapper || !document.querySelector("[data-article-root]")) return;

  const section = wrapper.closest(".portfolio-section");
  const projects = collectProjectsByCategoryKeys(categoryKeys);

  if (!projects.length) {
    if (section) section.hidden = true;
    wrapper.innerHTML = "";
    return;
  }

  if (section) section.hidden = false;
  renderProjectList(wrapper, projects);
  syncPortfolioSectionSwiper(wrapper, section);
}

function getCategoriesWithProjects(projects, categories) {
  return (categories || []).filter((category) =>
    (projects || []).some((project) => project.category === category.key),
  );
}

function formatPageNumber(value) {
  return value >= 10 ? String(value) : `0${value}`;
}

function getFilteredCategories() {
  const { categories, filterKey } = portfolioPageState;
  if (filterKey && filterKey !== FILTER_ALL) {
    return categories.filter((item) => item.key === filterKey);
  }
  return categories;
}

function getTotalPages(categoryCount) {
  return Math.max(1, Math.ceil(categoryCount / CATEGORIES_PER_PAGE));
}

function getPageCategories() {
  const filtered = getFilteredCategories();
  const totalPages = getTotalPages(filtered.length);
  const page = Math.min(Math.max(portfolioPageState.page, 1), totalPages);
  portfolioPageState.page = page;
  const start = (page - 1) * CATEGORIES_PER_PAGE;
  return filtered.slice(start, start + CATEGORIES_PER_PAGE);
}

function syncCategorySwiper(section) {
  if (!section) return;

  const currentSwiper = section.querySelector(".slide-row__swiper");
  const total = section.querySelector(".custom-count__total");
  const currentCount = section.querySelector(".custom-count__current");
  if (!currentSwiper || !total || !currentCount) return;

  if (currentSwiper.swiper) {
    currentSwiper.swiper.update();
    currentSwiper.swiper.slideTo(0, 0);
    if (currentCount) currentCount.textContent = "01";
    return;
  }

  const nextEls = Array.from(section.querySelectorAll(".slide-row__next"));
  const prevEls = Array.from(section.querySelectorAll(".slide-row__prev"));

  const instance = new Swiper(currentSwiper, {
    modules: [Navigation, Pagination],
    spaceBetween: 24,
    slidesPerView: "auto",
    preventClicks: false,
    preventClicksPropagation: false,
    navigation: {
      nextEl: nextEls,
      prevEl: prevEls,
    },
    pagination: {
      el: total,
      type: "custom",
      renderCustom(swiper, current, slidesTotal) {
        return formatPageNumber(slidesTotal);
      },
    },
  });

  instance.on("click", (swiper, event) => {
    const card = event.target.closest("[data-portfolio-project-id]");
    if (!card) return;
    event.preventDefault();
    openPortfolioProject(card.getAttribute("data-portfolio-project-id"));
  });

  instance.on("slideChange", () => {
    const inx = instance.realIndex + 1;
    gsap.to(currentCount, 0.3, {
      force3D: true,
      y: -5,
      opacity: 0,
      onComplete() {
        gsap.to(currentCount, 0.1, {
          force3D: true,
          y: 5,
        });
        currentCount.innerHTML = formatPageNumber(inx);
      },
    });
    gsap.to(currentCount, 0.3, {
      force3D: true,
      y: 0,
      opacity: 1,
      delay: 0.4,
    });
  });
}

function renderCategorySection(category) {
  return `
    <section class="advertising-section" data-portfolio-section="${escapeHtml(category.key)}">
      <div class="advertising-section__wrapper">
        <div class="container">
          <div class="advertising-section__introduction">
            <div class="introduction">
              <h2 class="introduction__title h3">${category.titleHtml}</h2>
              <div class="introduction__navigation">
                <div class="introduction__button-prev slide-row__prev">
                  <svg width="32" height="32" viewBox="0 0 32 32">
                    <use xlink:href="#icon-arrow-left"></use>
                  </svg>
                </div>
                <div class="introduction__button-next slide-row__next">
                  <svg width="32" height="32" viewBox="0 0 32 32">
                    <use xlink:href="#icon-arrow-right"></use>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="advertising-section__inner">
          <div class="slide-row">
            <div class="slide-row__swiper swiper">
              <div class="slide-row__swiper-wrapper swiper-wrapper" data-portfolio-category="${escapeHtml(category.key)}"></div>
            </div>
          </div>
          <div class="advertising-section__pagination-count">
            <div class="custom-count">
              <div class="custom-count__button-prev slide-row__prev">
                <svg width="32" height="32" viewBox="0 0 32 32">
                  <use xlink:href="#icon-arrow-left"></use>
                </svg>
              </div>
              <span class="custom-count__current h5">01</span>
              <span class="custom-count__line d1">|</span>
              <span class="custom-count__total d2"></span>
              <div class="custom-count__button-next slide-row__next">
                <svg width="32" height="32" viewBox="0 0 32 32">
                  <use xlink:href="#icon-arrow-right"></use>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  `;
}

function renderTagItem(key, title, isActive) {
  const safeKey = escapeHtml(key);
  const safeTitle = escapeHtml(title);
  if (isActive) {
    return `<span class="tags__link btn-s is-active" data-portfolio-tag="${safeKey}">${safeTitle}</span>`;
  }
  return `<button class="tags__link btn-s" type="button" data-portfolio-tag="${safeKey}">${safeTitle}</button>`;
}

function renderPortfolioTags(categories, filterKey) {
  const items = document.querySelector(".portfolio-page .tags__items");
  if (!items) return;

  if (!categories.length) {
    items.innerHTML = "";
    return;
  }

  const activeKey = filterKey || FILTER_ALL;
  const tags = [renderTagItem(FILTER_ALL, "Все", activeKey === FILTER_ALL)];
  categories.forEach((category) => {
    tags.push(renderTagItem(category.key, category.title, category.key === activeKey));
  });
  items.innerHTML = tags.join("");
}

function getPaginationPages(total, current) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const pages = new Set([1, total, current]);
  if (current - 1 > 1) pages.add(current - 1);
  if (current + 1 < total) pages.add(current + 1);
  if (current <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }
  if (current >= total - 2) {
    pages.add(total - 1);
    pages.add(total - 2);
    pages.add(total - 3);
  }

  return Array.from(pages).sort((a, b) => a - b);
}

function renderPortfolioPagination(totalPages, currentPage) {
  const container = document.querySelector(".portfolio-page .portfolio__pagination");
  if (!container) return;

  if (totalPages <= 1) {
    container.hidden = true;
    container.innerHTML = "";
    container.dataset.pages = "0";
    return;
  }

  container.hidden = false;

  const pagesSignature = String(totalPages);
  const canPatch = container.dataset.pages === pagesSignature
    && container.querySelector(".pagination");

  if (canPatch) {
    const prevBtn = container.querySelector("[data-portfolio-page-prev]");
    const nextBtn = container.querySelector("[data-portfolio-page-next]");
    if (prevBtn) prevBtn.disabled = currentPage <= 1;
    if (nextBtn) nextBtn.disabled = currentPage >= totalPages;

    const pages = getPaginationPages(totalPages, currentPage);
    const list = container.querySelector(".pagination__list");
    if (list) {
      let listHtml = "";
      let previous = 0;

      pages.forEach((page) => {
        if (previous && page - previous > 1) {
          listHtml += `
            <li class="pagination__item">
              <span class="pagination__link d1">&hellip;</span>
            </li>
          `;
        }

        if (page === currentPage) {
          listHtml += `
            <li class="pagination__item is-active">
              <span class="h4">${formatPageNumber(page)}</span>
              <span class="pagination__link" aria-current="page">
                <svg width="160" height="44" viewBox="0 0 160 44">
                  <use xlink:href="#icon-arrow-long"></use>
                </svg>
              </span>
            </li>
          `;
        } else {
          listHtml += `
            <li class="pagination__item">
              <button class="pagination__link d1" type="button" data-portfolio-page="${page}">${formatPageNumber(page)}</button>
            </li>
          `;
        }

        previous = page;
      });

      list.innerHTML = listHtml;
    }
    return;
  }

  const pages = getPaginationPages(totalPages, currentPage);
  let listHtml = "";
  let previous = 0;

  pages.forEach((page) => {
    if (previous && page - previous > 1) {
      listHtml += `
        <li class="pagination__item">
          <span class="pagination__link d1">&hellip;</span>
        </li>
      `;
    }

    if (page === currentPage) {
      listHtml += `
        <li class="pagination__item is-active">
          <span class="h4">${formatPageNumber(page)}</span>
          <span class="pagination__link" aria-current="page">
            <svg width="160" height="44" viewBox="0 0 160 44">
              <use xlink:href="#icon-arrow-long"></use>
            </svg>
          </span>
        </li>
      `;
    } else {
      listHtml += `
        <li class="pagination__item">
          <button class="pagination__link d1" type="button" data-portfolio-page="${page}">${formatPageNumber(page)}</button>
        </li>
      `;
    }

    previous = page;
  });

  container.dataset.pages = pagesSignature;
  container.innerHTML = `
    <div class="pagination">
      <button class="pagination__arrow pagination__arrow--left" type="button" data-portfolio-page-prev ${currentPage <= 1 ? "disabled" : ""}>
        <svg width="32" height="32" viewBox="0 0 32 32">
          <use xlink:href="#icon-arrow"></use>
        </svg>
      </button>
      <ul class="pagination__list">
        ${listHtml}
      </ul>
      <button class="pagination__arrow pagination__arrow--right" type="button" data-portfolio-page-next ${currentPage >= totalPages ? "disabled" : ""}>
        <svg width="32" height="32" viewBox="0 0 32 32">
          <use xlink:href="#icon-arrow"></use>
        </svg>
      </button>
    </div>
  `;
}

function lockPortfolioScroll() {
  const active = document.activeElement;
  if (active instanceof HTMLElement && active.closest(".portfolio-page")) {
    active.blur();
  }

  const root = document.querySelector("[data-portfolio-categories-root]");
  const pagination = document.querySelector(".portfolio-page .portfolio__pagination");
  const anchor = pagination && !pagination.hidden ? pagination : root;
  const anchorTop = anchor ? anchor.getBoundingClientRect().top : null;
  const rootHeight = root ? root.offsetHeight : 0;

  if (root && rootHeight > 0) {
    root.style.minHeight = `${rootHeight}px`;
  }

  return () => {
    const restore = () => {
      if (anchor && anchorTop != null && document.contains(anchor)) {
        const nextTop = anchor.getBoundingClientRect().top;
        const delta = nextTop - anchorTop;
        if (Math.abs(delta) > 1) {
          window.scrollBy(0, delta);
        }
      }

      if (root) {
        root.style.minHeight = `${root.scrollHeight}px`;
        window.requestAnimationFrame(() => {
          if (!root.isConnected) return;
          root.style.minHeight = "";
        });
      }
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(restore);
    });
  };
}

function ensureCategorySections(categories) {
  const root = document.querySelector("[data-portfolio-categories-root]");
  if (!root) return null;

  const signature = categories.map((item) => `${item.key}:${item.title}`).join("|");
  if (root.dataset.signature !== signature) {
    root.querySelectorAll(".slide-row__swiper").forEach((el) => {
      if (el.swiper) el.swiper.destroy(true, true);
    });
    root.innerHTML = categories.map((item) => renderCategorySection(item)).join("");
    root.dataset.signature = signature;
  }

  return root;
}

function applyPortfolioPageView({ updateTags = true } = {}) {
  const { projects, filterKey } = portfolioPageState;
  const root = document.querySelector("[data-portfolio-categories-root]");
  const filtered = getFilteredCategories();
  const unlockScroll = lockPortfolioScroll();

  if (!filtered.length) {
    if (root) {
      root.querySelectorAll(".slide-row__swiper").forEach((el) => {
        if (el.swiper) el.swiper.destroy(true, true);
      });
      root.innerHTML = "";
      delete root.dataset.signature;
    }
    if (updateTags) renderPortfolioTags(portfolioPageState.categories, filterKey);
    renderPortfolioPagination(0, 1);
    unlockScroll();
    return;
  }

  const totalPages = getTotalPages(filtered.length);
  const pageCategories = getPageCategories();

  ensureCategorySections(pageCategories);
  if (updateTags) renderPortfolioTags(portfolioPageState.categories, filterKey);
  renderPortfolioPagination(totalPages, portfolioPageState.page);

  pageCategories.forEach((category) => {
    const wrapper = document.querySelector(`[data-portfolio-category="${category.key}"]`);
    if (!wrapper) return;
    const list = projects.filter((item) => item.category === category.key);
    const section = wrapper.closest("[data-portfolio-section]");
    renderProjectList(wrapper, list);
    syncCategorySwiper(section);
  });

  unlockScroll();
}

function setPortfolioFilter(key) {
  const nextKey = key || FILTER_ALL;
  if (nextKey === portfolioPageState.filterKey) return;

  if (nextKey !== FILTER_ALL) {
    const exists = portfolioPageState.categories.some((item) => item.key === nextKey);
    if (!exists) return;
  }

  portfolioPageState.filterKey = nextKey;
  portfolioPageState.page = 1;
  applyPortfolioPageView({ updateTags: true });
}

function setPortfolioPage(page) {
  const filtered = getFilteredCategories();
  const totalPages = getTotalPages(filtered.length);
  if (!filtered.length) return;
  const nextPage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
  if (nextPage === portfolioPageState.page) return;
  portfolioPageState.page = nextPage;
  applyPortfolioPageView({ updateTags: false });
}

function bindPortfolioPageControls() {
  const pageRoot = document.querySelector(".portfolio-page");
  if (!pageRoot || pageRoot.dataset.portfolioBound === "true") return;
  pageRoot.dataset.portfolioBound = "true";

  pageRoot.addEventListener("click", (event) => {
    const projectCard = event.target.closest("[data-portfolio-project-id]");
    if (projectCard) {
      event.preventDefault();
      openPortfolioProject(projectCard.getAttribute("data-portfolio-project-id"));
      return;
    }

    const tag = event.target.closest("[data-portfolio-tag]");
    if (tag) {
      event.preventDefault();
      setPortfolioFilter(tag.getAttribute("data-portfolio-tag"));
      return;
    }

    const pageLink = event.target.closest("[data-portfolio-page]");
    if (pageLink) {
      event.preventDefault();
      setPortfolioPage(pageLink.getAttribute("data-portfolio-page"));
      return;
    }

    if (event.target.closest("[data-portfolio-page-prev]")) {
      event.preventDefault();
      setPortfolioPage(portfolioPageState.page - 1);
      return;
    }

    if (event.target.closest("[data-portfolio-page-next]")) {
      event.preventDefault();
      setPortfolioPage(portfolioPageState.page + 1);
    }
  });
}

function renderCategoryPortfolios(projects, categories) {
  const visibleCategories = getCategoriesWithProjects(projects, categories);
  portfolioPageState.projects = projects;
  portfolioPageState.categories = visibleCategories;

  if (!visibleCategories.length) {
    portfolioPageState.filterKey = FILTER_ALL;
    portfolioPageState.page = 1;
  } else if (
    portfolioPageState.filterKey !== FILTER_ALL
    && !visibleCategories.some((item) => item.key === portfolioPageState.filterKey)
  ) {
    portfolioPageState.filterKey = FILTER_ALL;
    portfolioPageState.page = 1;
  }

  bindPortfolioPageControls();
  applyPortfolioPageView();
}

function hydratePortfolio() {
  const data = getPortfolioData();
  renderHomePortfolio(data.projects);
  if (document.querySelector(".portfolio-page")) {
    renderCategoryPortfolios(data.projects, data.categories);
  }

  const first = data.projects[0];
  if (first) fillPortfolioPopup(first, 0);
}

document.addEventListener("nm:categories-updated", () => {
  hydratePortfolio();
});

document.addEventListener("nm:category-deleted", (event) => {
  const { deletedKey, fallbackKey } = event.detail || {};
  reassignProjectsCategory(deletedKey, fallbackKey);
  hydratePortfolio();
});

window.NMPortfolioContent = {
  STORAGE_KEY,
  DEFAULT_IMAGES,
  DEFAULT_CATEGORIES,
  get CATEGORIES() {
    return getCategoryMap();
  },
  get CATEGORY_KEYS() {
    return getCategoryKeys();
  },
  createId,
  escapeHtml,
  buildTitleHtml,
  normalizeCategory,
  descriptionToHtml,
  resolveProjectImage,
  resolveGallery,
  getPortfolioData,
  savePortfolioData,
  getDefaultData,
  getCategories,
  getCategoryMap,
  getCategoryKeys,
  normalizeProject,
  normalizeData,
  reassignProjectsCategory,
  renderServicePortfolio,
  renderArticlesPortfolio,
  fillPortfolioPopup,
  hydratePortfolio,
};

window.NMApi?.ready?.then(hydratePortfolio);
